// "As in" lines for homework spellings (Alan, 5 Oct: "How do I add the 'as in' text for
// homophones in homework? Can it be automatically added on upload"; "We have problems with
// 'write' and 'war' in a current homework"). A child hears a spelling word read aloud, and a
// word that sounds like another ("write", "right", "rite") cannot be spelled from the sound
// alone. LB-292 gave Learn & Earn's spelling words a description that is shown and spoken
// after the word; this is the same thing for homework.
//
// Where a line comes from, first match wins:
//   1. the published Learn & Earn spelling lists (Content Studio, LB-292's descriptions), so an
//      operator's wording is the one used everywhere, and the word's generated clip (which was
//      voiced WITH its description) is the one played;
//   2. the table below: the National Curriculum's homophones (English Appendix 1, Years 2 to 6)
//      and the everyday sound-alikes a spelling sheet is full of.
//
// THE RULE (LB-292): a line never contains its own word. It is shown on screen above the answer,
// so a line that included the word would hand over the spelling. scripts/check-homework.mjs
// holds every line here to that.
import { effectiveSpellingLists } from "./spelling.js";

// Words that sound alike, by group. A word in a group with no "as in" is worth a nudge.
export const SOUND_ALIKES = [
  ["there", "their", "they're"], ["here", "hear"], ["quite", "quiet"], ["see", "sea"], ["bare", "bear"],
  ["one", "won"], ["sun", "son"], ["to", "too", "two"], ["be", "bee"], ["blue", "blew"], ["night", "knight"],
  ["accept", "except"], ["affect", "effect"], ["ball", "bawl"], ["berry", "bury"], ["brake", "break"],
  ["fair", "fare"], ["grate", "great"], ["groan", "grown"], ["heel", "heal", "he'll"], ["knot", "not"],
  ["mail", "male"], ["main", "mane"], ["meat", "meet"], ["medal", "meddle"], ["missed", "mist"],
  ["peace", "piece"], ["plain", "plane"], ["rain", "rein", "reign"], ["scene", "seen"], ["weather", "whether"],
  ["whose", "who's"], ["aisle", "isle"], ["aloud", "allowed"], ["altar", "alter"], ["ascent", "assent"],
  ["bridal", "bridle"], ["cereal", "serial"], ["compliment", "complement"], ["descent", "dissent"],
  ["desert", "dessert"], ["draft", "draught"], ["farther", "father"], ["guessed", "guest"], ["heard", "herd"],
  ["led", "lead"], ["morning", "mourning"], ["past", "passed"], ["precede", "proceed"], ["principal", "principle"],
  ["profit", "prophet"], ["stationary", "stationery"], ["steal", "steel"], ["wary", "weary"],
  ["write", "right", "rite"], ["war", "wore"], ["for", "four"], ["new", "knew"], ["no", "know"], ["by", "buy", "bye"],
  ["flour", "flower"], ["hole", "whole"], ["hour", "our"], ["pair", "pear"], ["road", "rode"], ["sail", "sale"],
  ["tail", "tale"], ["wait", "weight"], ["week", "weak"], ["wood", "would"], ["which", "witch"], ["its", "it's"],
  ["your", "you're"], ["ate", "eight"], ["threw", "through"], ["made", "maid"], ["sew", "so", "sow"],
  ["sight", "site"], ["warn", "worn"], ["wear", "where"], ["allowed", "aloud"], ["knows", "nose"], ["sent", "scent", "cent"],
];

export const HOMOPHONE_HINTS = {
  there: "as in over in that place", their: "as in belonging to them", "they're": "as in they are",
  here: "as in in this place", hear: "as in to listen with your ears",
  quite: "as in fairly, or very", quiet: "as in making little noise",
  see: "as in to look with your eyes", sea: "as in the salty water by the beach",
  bare: "as in with nothing covering it", bear: "as in a big furry animal, or to carry",
  one: "as in the number 1", won: "as in came first in a game",
  sun: "as in the star that gives us daylight", son: "as in a boy who is someone's child",
  to: "as in going towards somewhere", too: "as in as well, or more than enough", two: "as in the number 2",
  be: "as in I want to ___ happy", bee: "as in an insect that makes honey",
  blue: "as in the colour of the sky", blew: "as in the wind moved the leaves",
  night: "as in when it is dark", knight: "as in a soldier in armour on a horse",
  accept: "as in to take something offered", except: "as in all but this one",
  affect: "as in to change something", effect: "as in the result of a change",
  ball: "as in a round thing you throw", bawl: "as in to cry loudly",
  berry: "as in a small soft fruit", bury: "as in to hide under the ground",
  brake: "as in to slow a bike down", break: "as in to snap, or a rest",
  fair: "as in equal for everyone", fare: "as in the price of a ticket",
  grate: "as in to shred cheese", great: "as in very good, or very big",
  groan: "as in a long low unhappy sound", grown: "as in got bigger",
  heel: "as in the back of your foot", heal: "as in to get better", "he'll": "as in he will",
  knot: "as in a tie in a piece of string", not: "as in no, the opposite of yes",
  mail: "as in letters and parcels", male: "as in a boy or a man",
  main: "as in the most important", mane: "as in the long hair on a horse's neck",
  meat: "as in food from an animal", meet: "as in to come together with someone",
  medal: "as in a prize for winning", meddle: "as in to interfere",
  missed: "as in did not catch it", mist: "as in light fog",
  peace: "as in calm, with no fighting", piece: "as in a part of something",
  plain: "as in simple, with nothing added", plane: "as in an aeroplane",
  rain: "as in water falling from clouds", rein: "as in the strap to steer a horse", reign: "as in a king or queen ruling",
  scene: "as in a part of a play", seen: "as in looked at already",
  weather: "as in sunny, windy or rainy", whether: "as in if, or not",
  whose: "as in belonging to which person", "who's": "as in who is",
  aisle: "as in the walkway between seats", isle: "as in a small island",
  aloud: "as in so others can hear", allowed: "as in let someone do it",
  altar: "as in a table in a church", alter: "as in to change",
  ascent: "as in a climb upwards", assent: "as in agreement",
  bridal: "as in to do with a bride", bridle: "as in the straps on a horse's head",
  cereal: "as in breakfast food in a bowl", serial: "as in a story in parts",
  compliment: "as in saying something nice", complement: "as in going well together",
  descent: "as in a way down", dissent: "as in disagreement",
  desert: "as in hot dry sandy land", dessert: "as in pudding after a meal",
  draft: "as in a first try at writing", draught: "as in cold air coming in",
  farther: "as in a longer way away", father: "as in a dad",
  guessed: "as in made a guess", guest: "as in a visitor",
  heard: "as in listened to", herd: "as in a group of cows",
  led: "as in showed the way", lead: "as in the metal, or a dog's strap",
  morning: "as in the start of the day", mourning: "as in feeling sad after a death",
  past: "as in time gone by", passed: "as in went by, or did well in a test",
  precede: "as in to come before", proceed: "as in to go on",
  principal: "as in head teacher, or most important", principle: "as in a rule you believe in",
  profit: "as in money made from selling", prophet: "as in someone who says what will happen",
  stationary: "as in not moving", stationery: "as in paper, pens and envelopes",
  steal: "as in to take without asking", steel: "as in a strong metal",
  wary: "as in careful, a bit worried", weary: "as in very tired",
  write: "as in to put words on paper with a pen", right: "as in correct, or the opposite of left", rite: "as in a special ceremony",
  war: "as in fighting between countries", wore: "as in had clothes on yesterday",
  for: "as in a present ___ you", four: "as in the number 4",
  new: "as in just made, not old", knew: "as in was sure of it",
  no: "as in the opposite of yes", know: "as in to be sure of something",
  by: "as in next to, or made ___ someone", buy: "as in to pay for something", bye: "as in goodbye",
  flour: "as in white powder for baking", flower: "as in a plant with petals",
  hole: "as in a gap or a dip", whole: "as in all of it",
  hour: "as in sixty minutes", our: "as in belonging to us",
  pair: "as in two of something", pear: "as in a green fruit",
  road: "as in a street for cars", rode: "as in went on a bike or a horse",
  sail: "as in the cloth on a boat", sale: "as in when things cost less",
  tail: "as in the end of an animal", tale: "as in a story",
  wait: "as in to stay until something happens", weight: "as in how heavy something is",
  week: "as in seven days", weak: "as in not strong",
  wood: "as in what trees are made of", would: "as in I ___ like that",
  which: "as in what one", witch: "as in someone who does magic in stories",
  its: "as in belonging to it", "it's": "as in it is",
  your: "as in belonging to you", "you're": "as in you are",
  ate: "as in had food", eight: "as in the number 8",
  threw: "as in tossed a ball", through: "as in in one side and out the other",
  made: "as in built or created", maid: "as in a servant who cleans",
  sew: "as in to stitch with a needle", so: "as in very, or and then", sow: "as in to plant seeds",
  sight: "as in being able to see", site: "as in a place where something is",
  warn: "as in to tell someone of danger", worn: "as in had on, or used a lot",
  wear: "as in to have clothes on", where: "as in in what place",
  knows: "as in is sure of", nose: "as in the part of your face you smell with",
  sent: "as in posted or passed on", scent: "as in a nice smell", cent: "as in a small coin",
};

const key = (w) => String(w || "").trim().toLowerCase().replace(/[’]/g, "'");

// Learn & Earn's own descriptions, from whatever spelling lists are published (or the starter
// lists when nothing is). Built on each call: it is a few hundred words and is only asked for
// when a list is being set.
function publishedDescriptions() {
  const out = new Map();
  const lists = effectiveSpellingLists() || {};
  for (const l of Object.values(lists)) {
    for (const w of (l && l.words) || []) {
      if (w && typeof w === "object" && typeof w.description === "string" && w.description.trim() && !out.has(key(w.word))) out.set(key(w.word), w.description.trim());
    }
  }
  return out;
}

// True when a line does not give its word away: the word never appears in it as a whole word.
export function hintIsSafe(word, hint) {
  const w = key(word);
  if (!w || !hint) return false;
  const esc = w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return !new RegExp(`(^|[^a-z'])${esc}($|[^a-z'])`, "i").test(String(hint).replace(/[’]/g, "'"));
}

// The "as in" line for a word, or "" when there is none. `published` is for tests.
export function hintFor(word, published = null) {
  const k = key(word);
  if (!k) return "";
  // A line that would give its own word away is skipped, and the next source tried.
  return [(published || publishedDescriptions()).get(k), HOMOPHONE_HINTS[k]].find((h) => h && hintIsSafe(k, h)) || "";
}

// The other words this one sounds like, for the word check's nudge.
export function soundAlikes(word) {
  const k = key(word);
  const group = SOUND_ALIKES.find((g) => g.includes(k));
  return group ? group.filter((x) => x !== k) : [];
}

// A spelling list's rows with an "as in" filled where one is known and the row has none.
// `hintAuto` marks a line the app filled, so it follows the word if the parent changes it and
// is left alone once they have typed their own (it is not saved: newHomeworkList keeps a, b).
export function withHints(pairs, published = null) {
  const pub = published || publishedDescriptions();
  return (pairs || []).map((p) => {
    if (!p || String(p.b || "").trim()) return p;
    const h = hintFor(p.a, pub);
    return h ? { ...p, b: h, hintAuto: true } : p;
  });
}
